/**
 * Document Processing Module
 * Text extraction, chunking, and keyword indexing for RAG
 */

import { db } from './db.js';
import { store } from './appState.js';
import { showToast } from './toast.js';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const CHUNK_SIZE = 2000; // ~500 tokens
const CHUNK_OVERLAP = 200; // ~50 tokens

const ACCEPTED_TYPES = {
  'text/plain': 'txt',
  'text/markdown': 'md',
  'application/json': 'json',
  'text/csv': 'csv',
  'application/pdf': 'pdf',
};

const ACCEPTED_EXTENSIONS = ['.txt', '.md', '.json', '.csv', '.pdf'];

/**
 * Check if a file type is accepted
 */
export function isAcceptedFile(file) {
  if (ACCEPTED_TYPES[file.type]) return true;
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  return ACCEPTED_EXTENSIONS.includes(ext);
}

/**
 * Get the accept attribute for file inputs
 */
export function getAcceptString() {
  return ACCEPTED_EXTENSIONS.join(',');
}

/**
 * Process an uploaded file: extract text, chunk, index, store
 * @param {File} file
 * @param {Object} options - { scope: 'global'|'agent'|'conversation', agentId?: string, conversationId?: string }
 * @returns {Promise<Object>} Document metadata
 */
export async function processFile(file, options = {}) {
  const { scope = 'global', agentId, conversationId } = options;
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 10MB.`);
  }

  if (!isAcceptedFile(file)) {
    throw new Error(`Unsupported file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`);
  }

  // Update upload progress
  store.setState({ knowledgeBase: { uploading: true, uploadProgress: 10 } });

  try {
    // Extract text
    const text = await extractText(file);
    store.setState({ knowledgeBase: { uploadProgress: 40 } });

    // Chunk text
    const rawChunks = chunkText(text);
    store.setState({ knowledgeBase: { uploadProgress: 60 } });

    // Build keyword index for each chunk
    const docId = crypto.randomUUID();
    const chunks = rawChunks.map((content, index) => ({
      id: `${docId}_chunk_${index}`,
      documentId: docId,
      index,
      content,
      keywords: buildKeywordIndex(content),
    }));
    store.setState({ knowledgeBase: { uploadProgress: 80 } });

    // Create document metadata
    const doc = {
      id: docId,
      name: file.name,
      size: file.size,
      type: getFileType(file),
      uploadDate: Date.now(),
      chunkCount: chunks.length,
      enabled: true,
      scope,
      agentId,
      conversationId,
    };

    // Save to IndexedDB
    await db.saveDocument(doc);
    await db.saveChunks(chunks);
    store.setState({ knowledgeBase: { uploadProgress: 100 } });

    // Update state
    const state = store.getState();
    store.setState({
      knowledgeBase: {
        documents: [...state.knowledgeBase.documents, doc],
        uploading: false,
        uploadProgress: 0,
      },
    });

    return doc;
  } catch (error) {
    store.setState({ knowledgeBase: { uploading: false, uploadProgress: 0 } });
    throw error;
  }
}

/**
 * Extract text content from a file
 */
async function extractText(file) {
  const type = getFileType(file);

  switch (type) {
    case 'txt':
    case 'md':
      return await file.text();

    case 'json': {
      const raw = await file.text();
      try {
        const parsed = JSON.parse(raw);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return raw;
      }
    }

    case 'csv': {
      const raw = await file.text();
      return parseCSVToText(raw);
    }

    case 'pdf': {
      const buffer = await file.arrayBuffer();
      return extractPDFText(buffer);
    }

    default:
      return await file.text();
  }
}

/**
 * Get file type from file object
 */
function getFileType(file) {
  if (ACCEPTED_TYPES[file.type]) return ACCEPTED_TYPES[file.type];
  const ext = file.name.split('.').pop().toLowerCase();
  return ext;
}

/**
 * Parse CSV content into readable text
 */
function parseCSVToText(csv) {
  const lines = csv.split('\n').filter(l => l.trim());
  if (lines.length === 0) return '';

  // Try to detect delimiter
  const firstLine = lines[0];
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  const rows = lines.map(line => {
    const cells = [];
    let current = '';
    let inQuotes = false;
    for (const ch of line) {
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === delimiter && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    return cells;
  });

  if (rows.length <= 1) return csv;

  const headers = rows[0];
  const dataRows = rows.slice(1);

  return dataRows.map((row, i) => {
    return `Row ${i + 1}: ` + headers.map((h, j) => `${h}: ${row[j] || ''}`).join(', ');
  }).join('\n');
}

/**
 * Extract readable text from PDF binary
 * Simple approach: find text between parentheses in PDF streams
 */
function extractPDFText(buffer) {
  const bytes = new Uint8Array(buffer);
  const text = [];

  // Decode bytes to string (handle ASCII/UTF-8)
  let raw = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b >= 32 && b < 127) {
      raw += String.fromCharCode(b);
    } else if (b === 10 || b === 13) {
      raw += '\n';
    } else {
      raw += ' ';
    }
  }

  // Extract text from PDF text objects: strings in parentheses after Tj/TJ operators
  // Pattern: (text) Tj or [(text)] TJ
  const parenRegex = /\(([^)]{1,500})\)/g;
  let match;
  while ((match = parenRegex.exec(raw)) !== null) {
    const content = match[1]
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '')
      .replace(/\\t/g, ' ')
      .replace(/\\\(/g, '(')
      .replace(/\\\)/g, ')')
      .replace(/\\\\/g, '\\')
      .trim();

    if (content.length > 1 && /[a-zA-Z0-9]/.test(content)) {
      text.push(content);
    }
  }

  if (text.length === 0) {
    // Fallback: extract any readable ASCII sequences
    const asciiRegex = /[a-zA-Z][a-zA-Z0-9 .,;:!?'"()-]{4,}/g;
    while ((match = asciiRegex.exec(raw)) !== null) {
      text.push(match[0].trim());
    }
  }

  return text.join(' ').replace(/\s+/g, ' ').trim() || 'Unable to extract text from PDF.';
}

/**
 * Chunk text into segments with overlap
 */
function chunkText(text) {
  if (text.length <= CHUNK_SIZE) return [text];

  const chunks = [];
  let start = 0;

  while (start < text.length) {
    let end = start + CHUNK_SIZE;

    // Try to break at sentence/paragraph boundary
    if (end < text.length) {
      const breakPoint = findBreakPoint(text, end);
      if (breakPoint > start + CHUNK_SIZE * 0.5) {
        end = breakPoint;
      }
    } else {
      end = text.length;
    }

    chunks.push(text.slice(start, end).trim());
    start = end - CHUNK_OVERLAP;
    if (start < 0) start = 0;
    if (end >= text.length) break;
  }

  return chunks.filter(c => c.length > 0);
}

/**
 * Find a good break point near the target position
 */
function findBreakPoint(text, target) {
  // Look for paragraph break
  const paraBreak = text.lastIndexOf('\n\n', target);
  if (paraBreak > target - 200 && paraBreak > 0) return paraBreak + 2;

  // Look for line break
  const lineBreak = text.lastIndexOf('\n', target);
  if (lineBreak > target - 100 && lineBreak > 0) return lineBreak + 1;

  // Look for sentence end
  const sentenceEnd = text.lastIndexOf('. ', target);
  if (sentenceEnd > target - 100 && sentenceEnd > 0) return sentenceEnd + 2;

  return target;
}

/**
 * Build keyword frequency index for a chunk
 */
function buildKeywordIndex(text) {
  const terms = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 2 && !STOP_WORDS.has(t));

  const freq = {};
  for (const term of terms) {
    freq[term] = (freq[term] || 0) + 1;
  }
  return freq;
}

/**
 * Load all documents from IndexedDB into state
 */
export async function loadStoredDocuments() {
  try {
    const documents = await db.getDocuments();
    store.setState({ knowledgeBase: { documents } });
  } catch (error) {
    console.error('Failed to load documents:', error);
  }
}

/**
 * Delete a document and update state
 */
export async function removeDocument(docId) {
  await db.deleteDocument(docId);
  const state = store.getState();
  store.setState({
    knowledgeBase: {
      documents: state.knowledgeBase.documents.filter(d => d.id !== docId),
    },
  });
}

/**
 * Toggle a document's enabled state
 */
export function toggleDocumentEnabled(docId) {
  const state = store.getState();
  const documents = state.knowledgeBase.documents.map(d =>
    d.id === docId ? { ...d, enabled: !d.enabled } : d
  );
  store.setState({ knowledgeBase: { documents } });
  // Persist toggle
  const doc = documents.find(d => d.id === docId);
  if (doc) db.saveDocument(doc);
}

/**
 * Get relevant context chunks for a query using active KB documents
 * @param {string} query - User message
 * @param {Object} options - { agentId?: string, conversationId?: string, attachedDocIds?: string[] }
 * @returns {Promise<Array<{docName: string, chunkIndex: number, totalChunks: number, content: string}>>}
 */
export async function getRAGContext(query, options = {}) {
  const { agentId, conversationId, attachedDocIds = [] } = options;
  const state = store.getState();

  // Collect active KB document IDs based on scope
  const kbDocIds = state.knowledgeBase.documents
    .filter(d => {
      if (!d.enabled) return false;
      // Global KB documents are always included
      if (d.scope === 'global') return true;
      // Agent-scoped documents only for matching agent
      if (d.scope === 'agent' && agentId && d.agentId === agentId) return true;
      // Conversation-scoped documents only for matching conversation
      if (d.scope === 'conversation' && conversationId && d.conversationId === conversationId) return true;
      return false;
    })
    .map(d => d.id);

  const allDocIds = [...new Set([...kbDocIds, ...attachedDocIds])];
  if (allDocIds.length === 0) return [];

  const results = await db.searchChunks(query, allDocIds);
  if (results.length === 0) return [];

  // Get doc names for display
  const docMap = {};
  for (const d of state.knowledgeBase.documents) {
    docMap[d.id] = d;
  }

  return results.map(({ chunk, score }) => ({
    docName: docMap[chunk.documentId]?.name || 'Unknown',
    docId: chunk.documentId,
    chunkIndex: chunk.index,
    totalChunks: docMap[chunk.documentId]?.chunkCount || 0,
    content: chunk.content,
    score,
  }));
}

// Common stop words to exclude from indexing
const STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had',
  'her', 'was', 'one', 'our', 'out', 'has', 'have', 'been', 'some', 'them',
  'than', 'its', 'over', 'such', 'that', 'this', 'with', 'will', 'each',
  'from', 'they', 'were', 'which', 'their', 'said', 'what', 'when', 'who',
  'would', 'there', 'about', 'into', 'more', 'other', 'then', 'these',
  'could', 'also', 'after', 'use', 'your', 'how', 'just', 'where', 'very',
  'should', 'still', 'between', 'being', 'does', 'those', 'because',
]);
